import React, { useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import { IPlan, IRhytNameData, ISolfanameData } from "../../../../../../common/types";
import { ISharedAudio } from "../../../../../../common/types/game/sharedAudio";
import { post } from "../../../../../actions/general";
import Icon from "../../../../../icon";
import { InputTextWrapper } from "../../../../../pages/game-admin/MelodyLiveGamesList/MelodyLiveGameEditor/styles";
import { editTask } from "../../../../../store/plan/actions";
import removeTaskProp from "../../../../../store/plan/actions/removeTaskProp";
import { useUser } from "../../../../../store/user/selectors";
import { Cancel, InputText, Title } from "../../../../Form";
import Modal from "../../../../Modal";
import { Changer } from "../../../EditorBar";
import { Label } from "../../common-style";
import AudioLinkerModal from "./AudioLinkerModal";
import { IProps } from "./interfaces";
import messages from "./messages";

const getAudioPathAndStart = (plan: IPlan, taskIndex: number, isSolution: boolean, isRhytName: boolean, rhytNameIndex: number) => {
    const taskData = plan.tasks[taskIndex].data;
    if (isRhytName) {
        const { audios = [] } = (taskData as IRhytNameData);
        if (audios[rhytNameIndex]) {
            return {
                audioPath: audios[rhytNameIndex].audio,
                audioStart: audios[rhytNameIndex].audioStart,
                audioShared: false,
            }
        }
        return {
            audioPath: undefined,
            audioStart: undefined,
            audioShared: false,
        }
    }
    return {
        audioPath: (taskData as ISolfanameData)[isSolution ? "audio2" : "audio"],
        audioStart: (taskData as ISolfanameData)[isSolution ? "audioStart2" : "audioStart"],
        audioShared: (taskData as ISolfanameData)[isSolution ? "audioShared2" : "audioShared"],
        audioSize: (taskData as ISolfanameData)[isSolution ? "audioSize2" : "audioSize"],
    }
}

const AudioUploader = ({ plan, taskIndex, isSolution, isRhytName, rhytNameIndex, linkDisabled }: IProps) => {
    const uploadRef = useRef<HTMLInputElement>(null);
    const [audioUploadInProgress, setAudioUploadInProgress] = useState(false);
    const [tooBigSize, setTooBigSize] = useState<null | number>(null);
    const [incorrectType, setIncorrectType] = useState(false);
    const [isAudioLinkerOpen, setIsAudioLinkerOpen] = useState(false);
    const user = useUser();
    const dispatch = useDispatch();
    const { audioPath, audioStart, audioShared, audioSize } = getAudioPathAndStart(plan, taskIndex, isSolution, isRhytName, rhytNameIndex);

    if (!user.isGameAdmin) return null;

    const handleUploadClick = () => {
        if (!audioUploadInProgress) {
            uploadRef.current.click()
        }
    }
    const handleUploadAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files!.length === 0) return;
        if (e.target.files![0].size > 1024 * 1024 * 10) {
            setTooBigSize(e.target.files![0].size)
            return;
        }
        if (
            e.target.files![0].type !== "audio/mpeg3" &&
            e.target.files![0].type !== "audio/mp3" &&
            e.target.files![0].type !== "audio/mpeg"
        ) {
            uploadRef.current.value = "";
            setIncorrectType(true);
        }
        const data = new FormData();

        data.append("audio", e.target.files![0]);
        data.append("planId", plan._id);
        data.append("index", taskIndex.toString());
        if (isRhytName) {
            data.append("isRhytName", "1");
        }
        if (isSolution) {
            data.append("isSolution", "1");
        }
        setAudioUploadInProgress(true);
        dispatch(post("upload-game-audio", data, res => {
            const { data } = res;
            setAudioUploadInProgress(false);
            if (data.file) {
                if (isRhytName) {
                    const newAudios = [...((plan.tasks[taskIndex].data as IRhytNameData).audios || []), {
                        audio: data.file,
                        types: [],
                    }];
                    dispatch(editTask({ taskIndex, changingProp: "audios", val: newAudios }));
                } else {
                    dispatch(editTask({ taskIndex, changingProp: isSolution ? "audio2" : "audio", val: data.file }));
                }
            }
        }));
    };

    const handleDeleteAudio = () => {
        if (audioShared) {
            dispatch(post("game-audio-unlink", { planId: plan._id, index: taskIndex, isSolution }, () => {
                dispatch(removeTaskProp({ taskIndex, removingProp: isSolution ? "audio2" : "audio" }));
                dispatch(removeTaskProp({ taskIndex, removingProp: isSolution ? "audioSize2" : "audioSize" }));
                dispatch(removeTaskProp({ taskIndex, removingProp: isSolution ? "audioShared2" : "audioShared" }));

            }))
        } else {
            dispatch(post("delete-game-audio", { planId: plan._id, index: taskIndex, isSolution, rhytNameIndex }, () => {
                if (isRhytName) {
                    const newAudios = [...(plan.tasks[taskIndex].data as IRhytNameData).audios];
                    newAudios.splice(rhytNameIndex, 1);
                    dispatch(editTask({ taskIndex, changingProp: "audios", val: newAudios }));
                } else {
                    dispatch(removeTaskProp({ taskIndex, removingProp: isSolution ? "audio2" : "audio" }));
                }
            }));
        }
    }

    const changeAudioMelodyStart = ({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
        const numberRegex = /^(\d?)+\.?(\d?)+$/i;
        if (!numberRegex.test(value)) {
            return;
        }
        if (isRhytName) {
            const newAudios = [...(plan.tasks[taskIndex].data as IRhytNameData).audios];
            newAudios[rhytNameIndex].audioStart = value as unknown as number;
            dispatch(editTask({ taskIndex, changingProp: "audios", val: newAudios }));
            if (!Number.isNaN(+value)) {
                dispatch(post(`game-audio-start`, {
                    audioStart: +value,
                    planId: plan._id,
                    index: taskIndex,
                    isSolution,
                    rhytNameIndex,
                }));
            }
        } else {
            dispatch(editTask({ taskIndex, changingProp: isSolution ? "audioStart2" : "audioStart", val: value }));
            if (!Number.isNaN(+value)) {
                dispatch(post(`game-audio-start`, {
                    audioStart: +value,
                    planId: plan._id,
                    index: taskIndex,
                    isSolution,
                }));
            }
        }
    }

    const handleShareAudio = () => {
        const title = window.prompt("Enter a name for the reference");
        if (!title) return;

        dispatch(post(`game/shared-audio`, {
            title,
            size: audioSize,
            filename: audioPath,
            audioStart,
        }, () => {
            dispatch(post("game-audio-link", {
                planId: plan._id,
                index: taskIndex,
                isSolution,
                filename: audioPath,
            }, () => {
                dispatch(editTask({ taskIndex, changingProp: isSolution ? "audioShared2" : "audioShared", val: true }));
            }))
        }));
    }

    const toggleAudioLinker = () => {
        setIsAudioLinkerOpen(open => !open);
    }

    const handleLinkAudio = (sharedAudio: ISharedAudio) => {
        toggleAudioLinker();
        dispatch(post("game-audio-link", {
            planId: plan._id,
            index: taskIndex,
            isSolution,
            filename: sharedAudio.filename,
        }, () => {
            dispatch(editTask({ taskIndex, changingProp: isSolution ? "audioShared2" : "audioShared", val: true }));
            dispatch(editTask({ taskIndex, changingProp: isSolution ? "audio2" : "audio", val: sharedAudio.filename }));
            dispatch(editTask({ taskIndex, changingProp: isSolution ? "audioSize2" : "audioSize", val: sharedAudio.size }));
            dispatch(editTask({ taskIndex, changingProp: isSolution ? "audioStart2" : "audioStart", val: sharedAudio.audioStart }));
        }))
    }
    return (
        <>
            {!audioPath && (
                <div onClick={handleUploadClick}>
                    <Changer disabled={audioUploadInProgress}>
                        <div>
                            <Icon i="upload-audio" />{" "}
                            <Label style={{ lineHeight: "normal" }}>
                                {audioUploadInProgress ? (
                                    <FormattedMessage {...messages.uploadAudioInProgress} />
                                ) : (
                                    <FormattedMessage {...messages.uploadAudio} />
                                )}
                                {isSolution && " 2"}
                            </Label>
                        </div>
                    </Changer>
                </div>
            )}
            {!!audioPath && (
                <div onClick={handleDeleteAudio}>
                    <Changer disabled={audioUploadInProgress}>
                        <div>
                            <Icon i={audioShared ? "share" : "upload-audio"} />{" "}
                            <Label style={{ lineHeight: "normal" }}>
                                {audioShared ? "Unlink" : <FormattedMessage {...messages.deleteAudio} />}
                                {isSolution && " 2"}
                            </Label>
                        </div>
                    </Changer>
                </div>
            )}
            {!audioPath && !linkDisabled && (
                <div onClick={toggleAudioLinker}>
                    <Changer disabled={audioUploadInProgress}>
                        <div>
                            <Icon i={"share"} />{" "}
                            <Label style={{ lineHeight: "normal" }}>
                                Link
                                {isSolution && " 2"}
                            </Label>
                        </div>
                    </Changer>
                </div>
            )}
            {!!audioPath && !isRhytName && !audioShared && audioStart?.toString().length > 0 && (
                <div onClick={handleShareAudio}>
                    <Changer>
                        <div>
                            <Icon i="share" />{" "}
                        </div>
                    </Changer>
                </div>
            )}
            {isAudioLinkerOpen && (
                <AudioLinkerModal close={toggleAudioLinker} handleLinkAudio={handleLinkAudio} />
            )}
            <input
                type="file"
                accept="audio/mpeg3"
                style={{ display: "none" }}
                ref={uploadRef}
                onChange={handleUploadAudioChange}
            />
            {tooBigSize && (
                <Modal close={() => setTooBigSize(null)}>
                    <Title>
                        <FormattedMessage {...messages.fileUpload} />
                    </Title>
                    <p>
                        <FormattedMessage {...messages.fileTooLarge} />: {Math.ceil((tooBigSize as number) / 1024)}{" "}
                        KB
                    </p>
                    <p>
                        <FormattedMessage {...messages.fileSizeLimit} values={{ value: "10" }} />
                    </p>
                    <Cancel onClick={() => setTooBigSize(null)}>
                        <FormattedMessage {...messages.close} />
                    </Cancel>
                </Modal>
            )}
            {incorrectType && (
                <Modal close={() => setIncorrectType(false)}>
                    <Title>
                        <FormattedMessage {...messages.fileUpload} />
                    </Title>
                    <p>
                        <FormattedMessage {...messages.fileIncorrectFormat} />
                    </p>
                    <p>
                        <FormattedMessage {...messages.uploadMp3} />
                    </p>
                    <Cancel onClick={() => setIncorrectType(false)}>
                        <FormattedMessage {...messages.close} />
                    </Cancel>
                </Modal>
            )}
            {audioPath && (
                <InputTextWrapper>
                    <div>
                        Start of melody:
                    </div>
                    <InputText
                        value={(audioStart || "").toString()}
                        name="audioMelodyStart"
                        onChange={changeAudioMelodyStart}
                    ></InputText>
                </InputTextWrapper>
            )}
        </>
    );
};

export default AudioUploader;
